import { useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type Encuesta = {
  id: number;
  titulo: string;
  descripcion: string | null;
  opciones: string[];
  fecha_fin: string | null;
  tipo: 'opciones' | 'abierta' | 'escala';
  max_opciones: number;
  mostrar_resultados: boolean;
  pedir_justificacion: boolean;
  escala_min: number;
  escala_max: number;
  total_votos: number;
  ya_voto: boolean;
  mi_voto: number | null;
  mis_opciones: number[] | null;
  mi_respuesta_escala: number | null;
  mi_respuesta_texto: string | null;
  mi_justificacion: string | null;
  conteo_votos: number[] | Record<number, number> | null;
};

function fmtFin(s: string | null): string | null {
  if (!s) return null;
  const diff = Math.ceil((new Date(s.replace(' ', 'T')).getTime() - Date.now()) / 86400000);
  if (diff <= 0) return 'Finalizada';
  if (diff === 1) return 'Último día';
  return `${diff} días restantes`;
}

export default function Encuestas() {
  const [items, setItems]       = useState<Encuesta[]>([]);
  const [cargando, setCargando] = useState(true);
  const [err, setErr]           = useState<string | null>(null);

  const [seleccion, setSeleccion] = useState<Record<number, number[]>>({});
  const [texto, setTexto]         = useState<Record<number, string>>({});
  const [escala, setEscala]       = useState<Record<number, number | null>>({});
  const [justif, setJustif]       = useState<Record<number, string>>({});
  const [enviando, setEnviando]   = useState<number | null>(null);
  const [errLocal, setErrLocal]   = useState<Record<number, string>>({});

  useEffect(() => {
    api<{ items: Encuesta[] }>('/encuestas')
      .then((d) => setItems(d.items))
      .catch((e) => setErr(e.message))
      .finally(() => setCargando(false));
  }, []);

  function toggleOpcion(encId: number, idx: number, maxOp: number) {
    setSeleccion((prev) => {
      const cur = prev[encId] ?? [];
      if (cur.includes(idx)) return { ...prev, [encId]: cur.filter((v) => v !== idx) };
      if (cur.length >= maxOp) return { ...prev, [encId]: [idx] };
      return { ...prev, [encId]: [...cur, idx] };
    });
  }

  async function votar(enc: Encuesta) {
    setErrLocal((p) => ({ ...p, [enc.id]: '' }));
    let body: Record<string, any> = {};

    if (enc.tipo === 'escala') {
      const val = escala[enc.id];
      if (val === null || val === undefined) return setErrLocal((p) => ({ ...p, [enc.id]: 'Seleccioná un valor' }));
      body = { respuesta_escala: val };
    } else if (enc.tipo === 'abierta') {
      if (!texto[enc.id]?.trim()) return setErrLocal((p) => ({ ...p, [enc.id]: 'Escribí tu respuesta' }));
      body = { respuesta_texto: texto[enc.id].trim() };
    } else if (enc.max_opciones > 1) {
      const sel = seleccion[enc.id] ?? [];
      if (!sel.length) return setErrLocal((p) => ({ ...p, [enc.id]: 'Seleccioná al menos una opción' }));
      body = { opciones_seleccionadas: sel };
    } else {
      const sel = seleccion[enc.id] ?? [];
      if (!sel.length) return setErrLocal((p) => ({ ...p, [enc.id]: 'Seleccioná una opción' }));
      body = { opcion_idx: sel[0] };
    }

    const j = justif[enc.id]?.trim();
    if (enc.pedir_justificacion && j) body.justificacion = j;

    setEnviando(enc.id);
    try {
      const resp = await api<any>(`/encuestas?id=${enc.id}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setItems((prev) =>
        prev.map((e) =>
          e.id === enc.id
            ? {
                ...e,
                ya_voto:             true,
                total_votos:         resp.total_votos ?? e.total_votos + 1,
                conteo_votos:        resp.conteo_votos,
                mostrar_resultados:  resp.mostrar_resultados ?? e.mostrar_resultados,
                mi_voto:             resp.mi_voto ?? null,
                mis_opciones:        resp.mis_opciones ?? null,
                mi_respuesta_escala: resp.mi_respuesta_escala ?? null,
                mi_respuesta_texto:  resp.mi_respuesta_texto ?? null,
              }
            : e
        )
      );
    } catch (e: any) {
      setErrLocal((p) => ({ ...p, [enc.id]: e.message || 'Error al votar' }));
    } finally {
      setEnviando(null);
    }
  }

  return (
    <>
      <TopBar title="Encuestas" back />
      <main className="max-w-md mx-auto px-4 py-4 pb-24 space-y-4">
        {cargando && <p className="text-center text-gray-400 pt-10">Cargando…</p>}
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
        )}
        {!cargando && items.length === 0 && (
          <div className="text-center py-14">
            <p className="text-4xl mb-3">🗳️</p>
            <p className="text-gray-500 text-sm">No hay encuestas activas por ahora.</p>
          </div>
        )}

        {items.map((enc) => {
          const fin = fmtFin(enc.fecha_fin);
          const sel = seleccion[enc.id] ?? [];

          return (
            <div key={enc.id} className="bg-white border border-app rounded-2xl p-4 space-y-3 shadow-sm">

              {/* Header */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold" style={{ color: '#32329f' }}>
                    {enc.total_votos} {enc.total_votos === 1 ? 'voto' : 'votos'}
                  </span>
                  {fin && (
                    <span className={`text-xs font-semibold ${
                      fin === 'Finalizada' ? 'text-gray-400'
                        : fin === 'Último día' ? 'text-red-500'
                        : 'text-gray-400'
                    }`}>
                      {fin}
                    </span>
                  )}
                </div>
                <p className="font-bold text-base text-gray-900 leading-snug">{enc.titulo}</p>
                {enc.descripcion && (
                  <p className="text-sm text-gray-500 mt-1 leading-snug">{enc.descripcion}</p>
                )}
              </div>

              {/* Resultado o votación */}
              {enc.ya_voto ? (
                <VotadoView enc={enc} />
              ) : (
                <>
                  {/* Opciones single/multi */}
                  {(enc.tipo === 'opciones' || !enc.tipo) && (
                    <div className="space-y-2">
                      {enc.max_opciones > 1 && (
                        <p className="text-xs text-gray-400">Podés elegir hasta {enc.max_opciones} opciones</p>
                      )}
                      {enc.opciones.map((op, i) => {
                        const marcada = sel.includes(i);
                        return (
                          <button
                            key={i}
                            onClick={() => toggleOpcion(enc.id, i, enc.max_opciones)}
                            className="w-full text-left px-3 py-2.5 rounded-xl border text-sm transition-colors"
                            style={marcada
                              ? { borderColor: '#32329f', backgroundColor: '#efeffa', color: '#32329f', fontWeight: 600 }
                              : { borderColor: '#e5e7eb', color: '#374151' }
                            }
                          >
                            <span className="mr-2">{marcada ? '●' : '○'}</span>{op}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Escala */}
                  {enc.tipo === 'escala' && (
                    <div>
                      <div className="flex gap-1.5 flex-wrap">
                        {Array.from(
                          { length: enc.escala_max - enc.escala_min + 1 },
                          (_, i) => enc.escala_min + i
                        ).map((v) => (
                          <button
                            key={v}
                            onClick={() => setEscala((p) => ({ ...p, [enc.id]: v }))}
                            className="w-10 h-10 rounded-xl text-sm font-bold border transition-colors"
                            style={escala[enc.id] === v
                              ? { borderColor: '#32329f', backgroundColor: '#32329f', color: '#ffffff' }
                              : { borderColor: '#e5e7eb', color: '#374151' }
                            }
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                        <span>Nada / No</span>
                        <span>Mucho / Sí</span>
                      </div>
                    </div>
                  )}

                  {/* Abierta */}
                  {enc.tipo === 'abierta' && (
                    <textarea
                      value={texto[enc.id] ?? ''}
                      onChange={(e) => setTexto((p) => ({ ...p, [enc.id]: e.target.value }))}
                      placeholder="Escribí tu respuesta…"
                      rows={3}
                      className="w-full rounded-xl px-3 py-2.5 text-sm outline-none resize-none"
                      style={{ border: '1px solid #6e6b6b', color: '#303030' }}
                    />
                  )}

                  {/* Justificación */}
                  {enc.pedir_justificacion && (
                    <textarea
                      value={justif[enc.id] ?? ''}
                      onChange={(e) => setJustif((p) => ({ ...p, [enc.id]: e.target.value }))}
                      placeholder="Comentario (opcional)…"
                      rows={2}
                      className="w-full rounded-xl px-3 py-2.5 text-sm outline-none resize-none"
                      style={{ border: '1px solid #d1d5db', color: '#303030' }}
                    />
                  )}

                  {errLocal[enc.id] && (
                    <p className="text-sm text-red-500">{errLocal[enc.id]}</p>
                  )}

                  <button
                    onClick={() => votar(enc)}
                    disabled={enviando === enc.id}
                    className="w-full text-white rounded-xl py-2.5 font-bold text-sm disabled:opacity-50"
                    style={{ backgroundColor: '#32329f' }}
                  >
                    {enviando === enc.id ? 'Enviando…' : 'Confirmar voto'}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </main>
    </>
  );
}

function VotadoView({ enc }: { enc: Encuesta }) {
  const total = enc.total_votos || 1;

  if (!enc.mostrar_resultados) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
        <p className="text-green-700 text-sm font-semibold">✓ Tu voto fue registrado</p>
        <p className="text-xs text-gray-400 mt-0.5">
          Los resultados se publicarán cuando finalice la encuesta.
        </p>
      </div>
    );
  }

  if (enc.tipo === 'abierta') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
        <p className="text-xs text-green-700 font-semibold mb-1">✓ Tu respuesta:</p>
        <p className="text-sm text-gray-700 italic">"{enc.mi_respuesta_texto}"</p>
      </div>
    );
  }

  if (enc.tipo === 'escala') {
    const dist = (enc.conteo_votos ?? {}) as Record<number, number>;
    return (
      <div className="space-y-2">
        <p className="text-xs text-green-600 font-semibold">✓ Tu respuesta: {enc.mi_respuesta_escala}</p>
        {Object.entries(dist)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([val, count]) => (
            <div key={val} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-5 flex-shrink-0">{val}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${Math.round((count / total) * 100)}%`, backgroundColor: '#32329f' }}
                />
              </div>
              <span className="text-xs text-gray-400 w-6 text-right">{count}</span>
            </div>
          ))}
      </div>
    );
  }

  // opciones (single y multi)
  const conteo  = (enc.conteo_votos ?? []) as number[];
  const misSel  = enc.mis_opciones ?? (enc.mi_voto !== null ? [enc.mi_voto] : []);

  return (
    <div className="space-y-2">
      <p className="text-xs text-green-600 font-semibold">✓ Tu voto fue registrado</p>
      {enc.opciones.map((op, i) => {
        const count = conteo[i] ?? 0;
        const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
        const esMio = misSel.includes(i);
        return (
          <div key={i}>
            <div className="flex justify-between text-sm mb-0.5">
              <span className={`flex-1 ${esMio ? 'font-bold' : 'text-gray-700'}`}
                    style={esMio ? { color: '#32329f' } : {}}>
                {esMio ? '● ' : '○ '}{op}
              </span>
              <span className="text-xs text-gray-400 ml-2">{pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 rounded-full"
                style={{ width: `${pct}%`, backgroundColor: esMio ? '#32329f' : '#d1d5db' }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
